import { Auth } from 'aws-amplify'
import request, { Response } from 'superagent'
import { API_ROOT } from './api-config'
import { check } from './util'
import { ApiError } from './types/common'
import type {
  CampaignsResponse,
  CommentEntity,
  BrandEntity,
  BrandsListResponse,
  UpdateBookingFlowResponse,
  InfluencersResponse,
  TransactionsResponse,
  CreditHistoriesResponse,
  ChatMessagesResponse,
  ChatConversationsResponse,
  ChatConversationEntity,
  ChatMessageEntity,
  SignedUrlResponse,
  TodoOverviewItemEntity,
  DashboardEntity,
  InvoiceGenerationResponse,
  FulfillmentResponse,
  RejectionResponse,
  ChatPreset
} from './types/api-types'

const handleErrors = async (err: unknown) => {
  console.error('API Error:', err)
  
  // Type guard for API errors
  if (err && typeof err === 'object' && 'response' in err) {
    const apiError = err as ApiError
    if (apiError.response && apiError.status === 401) {
      await Auth.signOut()
      localStorage.clear()
    }
  }
  
  return err
}

const responseBody = (res: Response) => res.body

export class Requests {
  constructor(public baseUrl: string) {}

  private async authHeaderValue() {
    const cognitoUser = await Auth.currentSession()
    return cognitoUser.getIdToken().getJwtToken()
  }

  replacePathAndQueryPlaceholders(url: string, dontCreateQuery: boolean, original: Record<string, unknown> | undefined): string {
    if (!original) return url
    
    const originalUrl = url
    const data = JSON.parse(JSON.stringify(original)) as Record<string, unknown>
    Object.keys(data).forEach(k => data[k] === '' && delete data[k]) // clean undefined values
    const entries = Object.entries(data)
    const matches: RegExpMatchArray[] = Array.from(url.matchAll(/{(.*?)}/g))
    
    for (const [match, key] of matches) {
      const [dataKey, value] = entries.find(([dataKey]) => key.toLowerCase() === dataKey.toLowerCase()) ?? []
      check(dataKey != null && typeof value === 'string', `Invalid path parameter for ${key} ${originalUrl}`)
      url = url.replace(match, value as string)
      delete data[dataKey]
    }
    
    if (dontCreateQuery) return url
    
    const q = new URLSearchParams(data as Record<string, string>).toString()
    if (q) url += `?${q}`
    return url
  }

  getCustom = <T = unknown>(url: string) => {
    return async (data: Record<string, unknown>): Promise<T> =>
      request
        .get(`${API_ROOT()}${this.replacePathAndQueryPlaceholders(url, false, data)}`)
        .set('Authorization', `${await this.authHeaderValue()}`)
        .on('error', handleErrors)
        .then(responseBody)
  }

  postCustom = <T = unknown>(url: string) => {
    return async (data: Record<string, unknown>): Promise<T> =>
      request
        .post(`${API_ROOT()}${this.replacePathAndQueryPlaceholders(url, true, data)}`, data as any)
        .set('Authorization', `${await this.authHeaderValue()}`)
        .set('Content-Type', 'application/json')
        .on('error', handleErrors)
        .then(responseBody)
  }

  patchCustom = <T = unknown>(url: string) => {
    return async (data: Record<string, unknown>): Promise<T> =>
      request
        .patch(`${API_ROOT()}${this.replacePathAndQueryPlaceholders(url, true, data)}`, data as any)
        .set('Authorization', `${await this.authHeaderValue()}`)
        .set('Content-Type', 'application/json')
        .on('error', handleErrors)
        .then(responseBody)
  }

  async del(url: string) {
    return request
      .del(`${this.baseUrl}${url}`)
      .set('Authorization', await this.authHeaderValue())
      .on('error', handleErrors)
      .then(responseBody)
  }

  async get<T = unknown>(url: string, body?: Record<string, unknown>): Promise<T> {
    if (body == null)
      return request
        .get(`${this.baseUrl}${url}`)
        .set('Authorization', await this.authHeaderValue())
        .on('error', handleErrors)
        .then(responseBody)
    return request
      .get(`${this.baseUrl}${url}`)
      .query({ body: JSON.stringify(body) })
      .set('Authorization', await this.authHeaderValue())
      .on('error', handleErrors)
      .then(responseBody)
  }

  async post<T = unknown>(url: string, body?: Record<string, unknown>): Promise<T> {
    return request
      .post(`${this.baseUrl}${url}`, body as any)
      .set('Authorization', await this.authHeaderValue())
      .set('Content-Type', 'application/json')
      .on('error', handleErrors)
      .then(responseBody)
  }
}
export const requests = new Requests(API_ROOT())

const Authentication = {
  current: () => Auth.currentAuthenticatedUser(),
  currentSession: () =>
    Auth.currentSession()
      .then(data => console.log(data))
      .catch(err => console.error(err)),
  login: (email: string, password: string) => Auth.signIn(email, password),
  completeNewPassword: (user: unknown, password: string) => Auth.completeNewPassword(user, password, {}),
  logout: () => Auth.signOut({ global: true })
}

const Campaigns = {
  getAll: requests.getCustom<CampaignsResponse>(`/admin/campaigns/v2`),
  getCampaignById: requests.getCustom<any>(`/admin/campaigns/v2/{campaignId}`),
  updateCampaign: requests.postCustom<void>(`/admin/campaigns/v2/{campaignId}`),
  verifyCampaign: requests.postCustom<void>(`/admin/business/{brandId}/{campaignId}/active`),
  setCampaignOffline: requests.postCustom<void>(`/admin/business/{brandid}/{campaignid}/offline`),
  searchCampaign: requests.postCustom<any[]>(`/admin/business/campaign`),
  setPriority: requests.postCustom<void>(`/admin/campaign/priority/{brandId}/{campaignId}`),
  getAllUneditedContent: requests.getCustom<any[]>(`/admin/getAllUneditedContent`),
  sendEditedContent: requests.postCustom<void>('/admin/sendEditedContent'),
  getContentCheck: requests.getCustom<any[]>(`/admin/content-check`),
  approveInfluencerContent: requests.postCustom<void>('/admin/ugc/approveContent'),
  getTodoOverview: requests.getCustom<TodoOverviewItemEntity[]>('/admin/getTodoOverview'),
  getArchivedTodos: requests.getCustom<TodoOverviewItemEntity[]>('/admin/getArchivedTodos'),
  archiveTodo: requests.postCustom<void>('/admin/archiveTodo'),
  unarchiveTodo: requests.postCustom<void>('/admin/unarchiveTodo'),
  setTodoEndDate: requests.postCustom<void>(`/admin/setTodoEndDate`),
  deleteCampaignInfluencer: requests.postCustom<void>(`/admin/delete-ci`),
  getContentForInternalFeedback: requests.getCustom<any[]>(`/admin/internal-feedback`),
  setInternalFeedback: requests.postCustom<void>(`/admin/internal-feedback`),
  deleteCampaign: requests.postCustom<void>(`/admin/campaign`),
  getMediaUploadUrl: requests.getCustom<SignedUrlResponse>(`/admin/reset/url`),
  getEditedMediaUploadUrl: requests.getCustom<SignedUrlResponse>(`/admin/edited/url`)
}

const Influencers = {
  delete: requests.postCustom<void>(`/admin/influencers/delete`),
  getAllNew: requests.getCustom<any[]>(`/admin/v2/influencers/new`),
  getAll: requests.postCustom<InfluencersResponse>(`/admin/influencers`),
  verifyUgc: requests.postCustom<void>(`/admin/influencers/{influencerid}/verified`),
  denyInfluencer: requests.postCustom<void>(`/admin/influencers/{influencerid}/deny`),
  toggleContentCv: requests.postCustom<void>(`/admin/influencers/content`),
  uploadCredit: requests.postCustom<void>(`/admin/influencers/{influencerid}/credit`),
  getInfluencer: requests.getCustom<any>(`/admin/influencers/{influencerid}`),
  getCampaignsOfCreator: requests.getCustom<any[]>(`/admin/influencers/{influencerid}/campaigns`),
  resetFeedback: requests.postCustom<void>(`/admin/influencers/resetfeedback`),
  getInvoices: requests.getCustom<any[]>(`/admin/influencers/{influencerId}/invoices`),
  updateInfluencer: requests.postCustom<void>(`/admin/influencers/{influencerId}`),
  spoofAsInfluencer: requests.postCustom<void>(`/admin/influencers/spoof`)
}

const Transactions = {
  getTransactions: requests.getCustom<TransactionsResponse>(`/admin/transactions`),
  generateInvoices: requests.postCustom<InvoiceGenerationResponse>('/admin/transactions/invoices'),
  markAsSent: requests.postCustom<FulfillmentResponse>('/admin/transactions/sent'),
  rejectTransactions: requests.postCustom<RejectionResponse>('/admin/transactions/reject'),
  getCreditHistories: requests.getCustom<CreditHistoriesResponse>(`/admin/credit-histories?influencerId={influencerId}`)
}

const Comments = {
  addComment: requests.postCustom<CommentEntity>(`/admin/comments`)
}

const Chat = {
  getConversations: requests.getCustom<ChatConversationsResponse>('/admin/chat/conversations'),
  createConversation: requests.postCustom<ChatConversationEntity>('/admin/chat/conversation'),
  getMessages: requests.getCustom<ChatMessagesResponse>('/admin/chat/messages'),
  sendMessage: requests.postCustom<ChatMessageEntity>('/admin/chat/message'),
  markAsRead: requests.postCustom<void>('/admin/chat/read'),
  markAsUnread: requests.postCustom<void>('/admin/chat/unread'),
  getChatMediaUploadUrl: requests.getCustom<SignedUrlResponse>('/admin/chat/message/url'),
  getChatPresets: requests.getCustom<ChatPreset[]>('/admin/chat/presets'),
  saveChatPreset: requests.postCustom<void>('/admin/chat/presets'),
  deleteChatPreset: requests.postCustom<void>('/admin/chat/presets/delete')
}

const Brands = {
  uploadBudget: requests.postCustom<void>(`/admin/business/{brandId}/budget/add`),
  getBrand: requests.getCustom<BrandEntity>(`/admin/business/{brandId}`),
  listBrands: requests.getCustom<BrandsListResponse>(`/admin/brands`),
  updateBookingFlow: requests.patchCustom<UpdateBookingFlowResponse>(`/admin/business/{brandId}/booking-flow`)
}

const Todo = {
  getTodoOverview: requests.getCustom<TodoOverviewItemEntity[]>('/admin/getTodoOverview'),
  setTodoEndDate: requests.postCustom<void>('/admin/setTodoEndDate'),
  resetUploadTodo: requests.postCustom<void>(`/admin/resetUploadTodo`),
  getDashboard: requests.getCustom<DashboardEntity[]>('/admin/dashboard')
}

export default {
  Authentication,
  Campaigns,
  Influencers,
  Transactions,
  Comments,
  Chat,
  Brands,
  Todo
}
